// Pure helpers for the Time-of-Week heatmap widget.
//
// Pipeline: backend returns raw hourly buckets (UTC) -> foldHourlyCounts() folds
// them into a 24x7 raw-count matrix in the viewer's timezone -> buildHeatmap()
// rebins to the chosen level of detail and normalizes each displayed day-column
// to sum to 1 (cell = share of that day's activity in that hour bin).
//
// The fold depends only on the timezone (recompute on tz change); the rebin
// depends only on container size (recompute on resize). Neither hits the network.

// Whole-hour IANA zones only. Half-hour / 45-min offsets (India, Iran, Nepal,
// parts of Australia) are intentionally omitted — they can't fold cleanly into
// 1h buckets, and the widget docs say so.
export const HEATMAP_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'America/Denver', label: 'US Mountain' },
  { value: 'America/Chicago', label: 'US Central' },
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Sao_Paulo', label: 'Brazil (São Paulo)' },
  { value: 'Europe/London', label: 'UK / Ireland' },
  { value: 'Europe/Berlin', label: 'Central Europe' },
  { value: 'Europe/Moscow', label: 'Moscow' },
  { value: 'Asia/Dubai', label: 'Gulf (Dubai)' },
  { value: 'Asia/Karachi', label: 'Pakistan' },
  { value: 'Asia/Shanghai', label: 'China' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Japan / Korea' },
  { value: 'Australia/Sydney', label: 'Australia East' },
  { value: 'Pacific/Auckland', label: 'New Zealand' }
]

// Resolve the viewer's browser timezone, falling back to UTC when it isn't a
// supported whole-hour zone (so the dropdown default always matches an option).
export function defaultTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && HEATMAP_TIMEZONES.some(z => z.value === tz)) return tz
  } catch (e) {
    // Intl unavailable — fall through to UTC.
  }
  return 'UTC'
}

// Monday-first day index. Intl weekday: 'short' gives Mon..Sun in en-US.
const WEEKDAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 }

// Fold raw hourly buckets into a 24x7 count matrix (rows = hour 0..23,
// cols = day 0..6 Mon..Sun) in the given timezone. DST is handled per bucket
// because each timestamp is formatted independently.
export function foldHourlyCounts(buckets, timeZone) {
  const counts = Array.from({ length: 24 }, () => new Array(7).fill(0))
  if (!Array.isArray(buckets) || buckets.length === 0) return counts

  let fmt
  try {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      hour12: false
    })
  } catch (e) {
    // Bad/unsupported tz — fall back to UTC so the widget still renders.
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      hour: '2-digit',
      hour12: false
    })
  }

  for (const b of buckets) {
    if (!b || b.ts == null) continue
    const parts = fmt.formatToParts(new Date(b.ts))
    let weekday = null
    let hour = null
    for (const p of parts) {
      if (p.type === 'weekday') weekday = WEEKDAY_INDEX[p.value]
      else if (p.type === 'hour') hour = parseInt(p.value, 10)
    }
    // '2-digit' hour12:false can emit '24' for midnight on some engines.
    if (hour === 24) hour = 0
    if (weekday == null || hour == null || Number.isNaN(hour)) continue
    counts[hour][weekday] += b.count || 0
  }
  return counts
}

// Hour bin levels: bin count must divide 24 evenly so bins are equal width.
// Finest first, coarsest last.
export const HOUR_LEVELS = [
  { bins: 24, span: 1 },
  { bins: 12, span: 2 },
  { bins: 8, span: 3 },
  { bins: 6, span: 4 },
  { bins: 4, span: 6 },
  { bins: 2, span: 12 }
]

// Day groupings, finest first. Groups hold Mon-first day indices.
export const DAY_LEVELS = [
  { cols: 7, groups: [[0], [1], [2], [3], [4], [5], [6]], labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
  { cols: 2, groups: [[0, 1, 2, 3, 4], [5, 6]], labels: ['Mon–Fri', 'Sat–Sun'] },
  { cols: 1, groups: [[0, 1, 2, 3, 4, 5, 6]], labels: ['All week'] }
]

const MIN_CELL = 16 // px; below this cells get unreadable
const AXIS_W = 44 // reserved for hour-row labels
const AXIS_H = 26 // reserved for day-column labels

// Choose the finest hour/day level of detail that fits the container. Falls back
// to the coarsest level when nothing fits.
export function pickLevels(widthPx, heightPx) {
  const usableH = (heightPx || 0) - AXIS_H
  const usableW = (widthPx || 0) - AXIS_W

  const hourLevel =
    HOUR_LEVELS.find(l => l.bins * MIN_CELL <= usableH) || HOUR_LEVELS[HOUR_LEVELS.length - 1]
  const dayLevel =
    DAY_LEVELS.find(l => l.cols * MIN_CELL <= usableW) || DAY_LEVELS[DAY_LEVELS.length - 1]

  return { hourLevel, dayLevel }
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

// Rebin a 24x7 count matrix to the chosen level of detail. The cell value is the
// raw event count; color is mapped globally (relative to the busiest bin in the
// whole grid) so the overall hotspot stands out. Returns echarts-ready data.
//   series item: [dayCol, hourRow, rawCount]   (raw count IS the mapped value)
// `max` is the largest raw count (visualMap ceiling); `total` is the grid sum
// (used for the tooltip's share-of-week).
export function buildHeatmap(counts, hourLevel, dayLevel) {
  const { span } = hourLevel
  const nRows = 24 / span
  const { groups, labels: xLabels } = dayLevel

  // Aggregate raw counts into [hourRow][dayCol].
  const cell = Array.from({ length: nRows }, () => new Array(groups.length).fill(0))
  for (let col = 0; col < groups.length; col++) {
    for (const day of groups[col]) {
      for (let hour = 0; hour < 24; hour++) {
        cell[Math.floor(hour / span)][col] += counts[hour][day]
      }
    }
  }

  const series = []
  let max = 0
  let total = 0
  for (let row = 0; row < nRows; row++) {
    for (let col = 0; col < groups.length; col++) {
      const raw = cell[row][col]
      if (raw > max) max = raw
      total += raw
      series.push([col, row, raw])
    }
  }

  // Hour-bin labels: single hours when span === 1, else ranges.
  const yLabels = []
  for (let row = 0; row < nRows; row++) {
    const start = row * span
    yLabels.push(span === 1 ? pad2(start) : `${pad2(start)}–${pad2(start + span - 1)}`)
  }

  return { series, max, total, xLabels, yLabels }
}
